import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "@/lib/useNav";
import { formatDateShort, formatSize } from "@/lib/format";
import { PipelineBoard } from "@/components/opportunities/PipelineBoard";
import { StagePill } from "@/components/opportunities/StagePill";
import { CampaignStatusPill } from "@/components/modals/CampaignModal";
import { CalendarView } from "@/pages/Campaigns";
import { CollabBoard, CollabCalendar } from "@/components/shared/CollabViews";
import { CollabStatusPill, CollabIcon, collabDates } from "@/lib/collabs";
import { useBlockData, BlockEmpty } from "@/components/dashboard/useBlockData";

function Row({ onClick, testid, title, sub, right }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">{title}</p>
        {sub && <p className="truncate text-xs text-slate-400">{sub}</p>}
      </div>
      {right}
    </button>
  );
}

export function OpportunitiesBlock({ block }) {
  const s = block.settings || {};
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  useEffect(() => { client.get("/pipeline/stages").then(({ data }) => setStages(data.stages)).catch(() => {}); }, []);
  const [opps, load, setOpps] = useBlockData(() => client.get("/opportunities", { params: { owner_id: s.scope === "mine" ? user.id : undefined } }).then((r) => r.data), [s.scope, user.id]);
  if (!opps) return null;
  const changeStage = async (id, stageId) => {
    setOpps((prev) => prev.map((o) => (o.id === id ? { ...o, stage_id: stageId } : o)));
    try { const { data } = await client.patch(`/opportunities/${id}`, { stage_id: stageId }); toast.success(`Moved to ${data.stage_name}`); }
    catch (e) { toast.error("Could not move deal"); load(); }
  };
  if (s.view === "pipeline") {
    return <PipelineBoard stages={stages} opps={opps} onChange={changeStage} onOpen={(o) => navigate(`/opportunities/${o.id || o}`)} onAddCard={() => navigate("/opportunities")} />;
  }
  if (!opps.length) return <BlockEmpty>No opportunities</BlockEmpty>;
  const byId = Object.fromEntries(stages.map((st) => [st.id, st]));
  return (
    <div className="space-y-0.5">
      {opps.slice(0, 30).map((o) => {
        const st = byId[o.stage_id] || {};
        return <Row key={o.id} onClick={() => navigate(`/opportunities/${o.id}`)} testid={`block-${block.id}-opp-${o.id}`} title={o.title} sub={formatSize(o)}
          right={<StagePill name={st.name || o.stage_name} isWon={st.is_won} isLost={st.is_lost} color={st.color} />} />;
      })}
    </div>
  );
}

const DONE_CAMPAIGN = ["Completed", "Cancelled"];
export function CampaignsBlock({ block }) {
  const s = block.settings || {};
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows] = useBlockData(() => client.get("/campaigns", { params: { user_id: s.scope === "mine" ? user.id : undefined } }).then((r) => r.data), [s.scope, user.id]);
  if (!rows) return null;
  const list = rows.filter((c) => {
    const isEmail = (c.campaign_type || "Agent") === "Email";
    if (s.kind === "edm" && !isEmail) return false;
    if (s.kind === "agent" && isEmail) return false;
    return !(s.hideDone && DONE_CAMPAIGN.includes(c.effective_status));
  });
  const open = (c) => navigate(`/campaigns/${(c.campaign_type || "Agent") === "Email" ? "direct" : "agent"}?open=${c.id}`);
  if (s.view === "calendar") return <CalendarView campaigns={list} onOpen={open} isEDM={s.kind === "edm"} />;
  if (!list.length) return <BlockEmpty>No campaigns</BlockEmpty>;
  return (
    <div className="space-y-0.5">
      {list.slice(0, 40).map((c) => (
        <Row key={c.id} onClick={() => open(c)} testid={`block-${block.id}-campaign-${c.id}`} title={c.name}
          sub={[c.campaign_type || "Agent", c.property_name, c.on_sale_start && formatDateShort(c.on_sale_start)].filter(Boolean).join(" · ")}
          right={<CampaignStatusPill status={c.effective_status} />} />
      ))}
    </div>
  );
}

const DONE_COLLAB = ["Completed", "Ended", "Declined", "Cancelled"];
export function CollabsBlock({ block }) {
  const s = block.settings || {};
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, load] = useBlockData(() => client.get("/collaborations", { params: { collab_type: s.collabType || undefined } }).then((r) => r.data), [s.collabType]);
  if (!rows) return null;
  const list = rows.filter((c) => (s.scope !== "mine" || (c.assigned_to_ids || []).includes(user.id)) && !(s.hideDone && DONE_COLLAB.includes(c.status)));
  const open = (c) => navigate(`/collaborations?open=${c.id}`);
  if (s.view === "calendar") return <CollabCalendar items={list} onOpen={open} />;
  if (s.view === "board") return <CollabBoard items={list} type={s.collabType || undefined} onOpen={open} onChanged={load} />;
  if (!list.length) return <BlockEmpty>No collaborations</BlockEmpty>;
  return (
    <div className="space-y-0.5">
      {list.slice(0, 40).map((c) => {
        return (
          <Row key={c.id} onClick={() => open(c)} testid={`block-${block.id}-collab-${c.id}`} title={<span className="inline-flex items-center gap-1.5"><CollabIcon type={c.collab_type} className="h-3.5 w-3.5" />{c.title}</span>}
            sub={[c.property_names?.join(", "), collabDates(c)].filter(Boolean).join(" · ")}
            right={<CollabStatusPill status={c.status} />} />
        );
      })}
    </div>
  );
}
